import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabase';
import { sendRescheduleEmail } from "../utils/EmailSender";
import { v4 as uuidv4 } from 'uuid';
import { serviceRequestsApi, appointmentsApi, userProfilesApi } from '../lib/api';
// Import the centralized API module

const {
  FiHome, FiPlus, FiClock, FiCheckCircle, FiAlertCircle,
  FiTrendingUp, FiCalendar, FiPhone, FiUsers, FiBarChart2, FiSettings, FiArrowRight, FiX
} = FiIcons;

// --------------------------------------------------------------------
// RequestDetailModal Component (No changes needed)
// --------------------------------------------------------------------
const RequestDetailModal = ({ request, onClose, onStatusUpdate, userRole }) => {
  if (!request) return null;

  const currentStatus = request.status?.toLowerCase();
  const canUpdateStatus = userRole === 'admin' && currentStatus !== 'completed' && currentStatus !== 'rejected';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <SafeIcon icon={FiX} className="text-2xl" />
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-3">Request Details</h2>

        <div className="space-y-3 text-gray-700">
          <p><strong>Service Type:</strong> {request.service_type
            ?.replace(/-/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase())}</p>
          <p><strong>Description:</strong> {request.description || 'No description provided.'}</p>
          <p><strong>Status:</strong> <span className={`font-semibold ${currentStatus === 'pending' ? 'text-yellow-600' :
            currentStatus === 'accepted' || currentStatus === 'approved' ? 'text-green-600' :
              currentStatus === 'in progress' ? 'text-blue-600' :
                currentStatus === 'completed' ? 'text-emerald-600' :
                  currentStatus === 'rejected' ? 'text-red-600' : 'text-gray-600'
            }}`}>{request.status}</span></p>
          <p><strong>Created:</strong> {new Date(request.created_at).toLocaleString()}</p>
          <p><strong>Due Date:</strong> {new Date(request.end_date).toLocaleDateString()}</p>
          {userRole === 'admin' && request.user_id && (
            <p><strong>Requested By User ID:</strong> {request.user_id}</p>
          )}
        </div>

        {canUpdateStatus && (
          <div className="mt-6">
            <p className="text-sm font-semibold text-gray-600 mb-3">Update Status:</p>
            <div className="grid grid-cols-2 gap-3">
              {currentStatus === 'pending' && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onStatusUpdate(request.id, 'approved')}
                    className="px-4 py-3 bg-green-500 text-white rounded-lg font-semibold shadow-md hover:bg-green-600 transition-colors"
                  >
                    Approve
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onStatusUpdate(request.id, 'rejected')}
                    className="px-4 py-3 bg-red-500 text-white rounded-lg font-semibold shadow-md hover:bg-red-600 transition-colors"
                  >
                    Reject
                  </motion.button>
                </>
              )}
              {(currentStatus === 'accepted' || currentStatus === 'approved') && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onStatusUpdate(request.id, 'in progress')}
                    className="px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold shadow-md hover:bg-blue-600 transition-colors col-span-2"
                  >
                    Mark In Progress
                  </motion.button>
                </>
              )}
              {currentStatus === 'in_progress' && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onStatusUpdate(request.id, 'completed')}
                    className="px-4 py-3 bg-emerald-500 text-white rounded-lg font-semibold shadow-md hover:bg-emerald-600 transition-colors col-span-2"
                  >
                    Mark Completed
                  </motion.button>
                </>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// --------------------------------------------------------------------
// AdminAddRequestModal Component (Professional & Highly Compatible UI)
// --------------------------------------------------------------------
const AdminAddRequestModal = ({ isOpen, onClose, customers, onSuccess }) => {
  const [userId, setUserId] = useState('');
  const [serviceType, setServiceType] = useState('parental-care');
  const [howReachedUs, setHowReachedUs] = useState('WhatsApp');
  const [urgency, setUrgency] = useState('Medium');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      setErrorMsg('Please select a customer.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg('');

      const fullDescription = description
        ? `${description}\n\n[Lead Source / Reached Us Via: ${howReachedUs}]`
        : `[Lead Source / Reached Us Via: ${howReachedUs}]`;

      const payload = {
        user_id: userId,
        service_type: serviceType,
        urgency: urgency,
        status: 'pending',
        description: fullDescription,
        how_reached_us: howReachedUs,
        start_date: startDate || new Date().toISOString(),
        end_date: endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      };

      const { data, error } = await supabase
        .from('service_requests')
        .insert([payload])
        .select()
        .single();

      if (error) {
        if (error.message?.includes('how_reached_us')) {
          delete payload.how_reached_us;
          const { data: retryData, error: retryErr } = await supabase
            .from('service_requests')
            .insert([payload])
            .select()
            .single();
          if (retryErr) throw retryErr;
          onSuccess(retryData);
        } else {
          throw error;
        }
      } else {
        onSuccess(data);
      }

      onClose();
    } catch (err) {
      console.error('Error creating request:', err);
      setErrorMsg(err.message || 'Failed to create service request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 20, opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 relative my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-white px-6 sm:px-8 py-6 relative flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white shadow-md flex-shrink-0">
              <SafeIcon icon={FiPlus} className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                Add Request for Customer
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Create a new service request on behalf of a registered customer.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-all"
            aria-label="Close modal"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Modal Body Form */}
        <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
          {errorMsg && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-lg text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Unable to submit request</p>
                <p className="text-xs mt-0.5 opacity-90">{errorMsg}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 1. Customer Selection */}
            <div>
              <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiUsers} className="text-indigo-600" />
                Select Customer <span className="text-red-500 font-bold">*</span>
              </label>
              <div className="relative">
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all appearance-none cursor-pointer shadow-sm"
                >
                  <option value="">-- Choose Customer (Name / Email) --</option>
                  {customers.map((cust) => (
                    <option key={cust.id} value={cust.id}>
                      {cust.full_name ? `${cust.full_name} (${cust.email || 'No Email'})` : cust.email || cust.id}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                  ▼
                </div>
              </div>
            </div>

            {/* 2. Responsive 2-Column Row: Service Type & Lead Source */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiHome} className="text-indigo-600" />
                  Service Type
                </label>
                <div className="relative">
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all appearance-none cursor-pointer shadow-sm"
                  >
                    <option value="parental-care">Parental Care</option>
                    <option value="property-care">Property Care</option>
                    <option value="celebration-services">Celebrate & Events</option>
                    <option value="financial-services">Financial & Legal Advisory</option>
                    <option value="travel-services">Travel Services</option>
                    <option value="custom-services">Custom Services</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                    ▼
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiPhone} className="text-indigo-600" />
                  How They Reached Us (Lead Source)
                </label>
                <div className="relative">
                  <select
                    value={howReachedUs}
                    onChange={(e) => setHowReachedUs(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all appearance-none cursor-pointer shadow-sm"
                  >
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="Website Enquiry">Website Enquiry</option>
                    <option value="Referral">Referral</option>
                    <option value="Social Media">Social Media (Instagram / FB / LinkedIn)</option>
                    <option value="Email">Email</option>
                    <option value="Direct Walk-in">Direct Walk-in / Word of Mouth</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                    ▼
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Responsive 3-Column Row: Urgency & Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiAlertCircle} className="text-indigo-600" />
                  Urgency
                </label>
                <div className="relative">
                  <select
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all appearance-none cursor-pointer shadow-sm"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                    ▼
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiCalendar} className="text-indigo-600" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate ? startDate.substring(0, 10) : ''}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all shadow-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                  <SafeIcon icon={FiClock} className="text-indigo-600" />
                  Due / End Date
                </label>
                <input
                  type="date"
                  value={endDate ? endDate.substring(0, 10) : ''}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all shadow-sm"
                />
              </div>
            </div>

            {/* 4. Request Description */}
            <div>
              <label className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiBarChart2} className="text-indigo-600" />
                Request Details / Notes
              </label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter details of what the customer requested..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 text-sm font-medium transition-all resize-none placeholder:text-gray-400 shadow-sm"
              />
            </div>

            {/* 5. Modal Footer CTA Bar */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCheckCircle} className="text-base" />
                    <span>Create Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

// --------------------------------------------------------------------
// Dashboard Component
// --------------------------------------------------------------------
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [role, setRole] = useState(null);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [activeRequests, setActiveRequests] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [adminStats, setAdminStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [enquiredUsers, setEnquiredUsers] = useState([]);
  const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeNotification, setActiveNotification] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [userInvoices, setUserInvoices] = useState([]);
  const [allInvoicesAdmin, setAllInvoicesAdmin] = useState([]);

  useEffect(() => {
    setShowRescheduleModal(false);
  }, []);

  const syncAdminInvoices = async () => {
    try {
      const { data: dbInv } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });
      const localInv = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
      const combinedMap = new Map();
      if (dbInv) dbInv.forEach(i => combinedMap.set(i.id, i));
      localInv.forEach(i => {
        if (combinedMap.has(i.id)) {
          const existing = combinedMap.get(i.id);
          if (i.status === 'paid' || existing.status === 'paid') {
            combinedMap.set(i.id, { ...existing, ...i, status: 'paid', paid_at: i.paid_at || existing.paid_at });
          }
        } else {
          combinedMap.set(i.id, i);
        }
      });
      setAllInvoicesAdmin(Array.from(combinedMap.values()));
    } catch (e) {
      const localInv = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
      setAllInvoicesAdmin(localInv);
    }
  };

  useEffect(() => {
    const reloadInvoices = () => {
      if (user?.id) {
        const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
        const matched = local.filter(inv =>
          inv.user_id === user.id ||
          (inv.user_email && user.email && inv.user_email.toLowerCase() === user.email.toLowerCase())
        );
        const listToDisplay = matched.length > 0 ? matched : local;
        if (listToDisplay.length > 0) {
          setUserInvoices(listToDisplay);
        }
      }
      syncAdminInvoices();
    };
    reloadInvoices();
    window.addEventListener('storage', reloadInvoices);
    window.addEventListener('nlb_invoice_updated', reloadInvoices);
    return () => {
      window.removeEventListener('storage', reloadInvoices);
      window.removeEventListener('nlb_invoice_updated', reloadInvoices);
    };
  }, [user?.id, user?.email, role]);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newStartTime, setNewStartTime] = useState('');

  useEffect(() => {
    const checkTime = setInterval(() => {
      const now = new Date();
      upcomingAppointments.forEach(app => {
        const appTime = new Date(app.start_time);
        // If appointment started in the last 1 minute and is still scheduled
        if (now >= appTime && (now - appTime) < 60000 && app.status === 'scheduled') {
          setActiveNotification(app);
        }
      });
    }, 10000); // Check every 10 seconds
    return () => clearInterval(checkTime);
  }, [upcomingAppointments]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }



    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        console.log("Checking role for user:", user.id);

        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        let currentRole = profile?.role || 'user';
        let providerRec = null;

        console.log("Role from profile table:", currentRole);

        const { data: provider } = await supabase
          .from('providers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();


        if (provider) {
          currentRole = 'provider';
        }

        console.log("Final determined role:", currentRole);
        setRole(currentRole);


        // 🔹 Fetch enquired users (for admin)
        const { data: requestUsers, error: requestUsersError } = await supabase
          .from('request_user')
          .select('*')
          .order('created_at', { ascending: false });

        if (requestUsersError) console.error('Error fetching enquired users:', requestUsersError.message);
        else setEnquiredUsers(requestUsers || []);

        // 🔹 Fetch data based on role
        if (currentRole === 'admin') {
          const { data: allProfiles } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .order('full_name', { ascending: true });
          setAllCustomers(allProfiles || []);

          try {
            const { data: dbInv } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });
            const localInv = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
            const combinedMap = new Map();
            if (dbInv) dbInv.forEach(i => combinedMap.set(i.id, i));
            localInv.forEach(i => {
              if (combinedMap.has(i.id)) {
                const existing = combinedMap.get(i.id);
                if (i.status === 'paid' || existing.status === 'paid') {
                  combinedMap.set(i.id, { ...existing, ...i, status: 'paid', paid_at: i.paid_at || existing.paid_at });
                }
              } else {
                combinedMap.set(i.id, i);
              }
            });
            setAllInvoicesAdmin(Array.from(combinedMap.values()));
          } catch (e) {
            const localInv = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
            setAllInvoicesAdmin(localInv);
          }
          // =========================
          // ADMIN STATS
          // =========================

          const [usersRes, requestsRes, appointmentsRes] =
            await Promise.all([
              supabase
                .from('profiles')
                .select('*', {
                  count: 'exact',
                  head: true,
                }),

              supabase
                .from('service_requests')
                .select('*', {
                  count: 'exact',
                  head: true,
                }),

              supabase
                .from('appointments')
                .select('*', {
                  count: 'exact',
                  head: true,
                }),
            ]);

          setAdminStats({
            userCount: usersRes.count || 0,
            requestCount: requestsRes.count || 0,
            appointmentCount:
              appointmentsRes.count || 0,
          });

          // =========================
          // FETCH ALL REQUESTS
          // =========================

          const {
            data: allRequests,
            error: allRequestsError,
          } = await supabase
            .from('service_requests')
            .select('*')
            .order('created_at', {
              ascending: false,
            });

          if (allRequestsError) {
            console.error(
              'Error fetching admin requests:',
              allRequestsError.message
            );
          }

          setActiveRequests(allRequests || []);

          // =========================
          // CUSTOMER GROUPING
          // =========================

          const groupedCustomers = {};

          (allRequests || []).forEach((request) => {
            const userId = request.user_id;

            if (!userId) return;

            if (!groupedCustomers[userId]) {
              groupedCustomers[userId] = {
                user_id: userId,
                name: '',
                email: '',
                requests: [],
                total_requests: 0,
                pending: 0,
                completed: 0,
                in_progress: 0,
              };
            }

            groupedCustomers[userId].requests.push(
              request
            );

            groupedCustomers[userId]
              .total_requests++;

            const status =
              request.status?.toLowerCase();

            if (status === 'pending') {
              groupedCustomers[userId].pending++;
            }

            if (status === 'completed') {
              groupedCustomers[userId].completed++;
            }

            if (
              status === 'in_progress' ||
              status === 'in progress'
            ) {
              groupedCustomers[userId]
                .in_progress++;
            }
          });

          console.log(
            'Grouped Customers Before Profiles:',
            groupedCustomers
          );

          // =========================
          // FETCH CUSTOMER DETAILS
          // =========================

          const customerIds =
            Object.keys(groupedCustomers);

          if (customerIds.length > 0) {
            const {
              data: customerProfiles,
              error: profileError,
            } = await supabase
              .from('profiles')
              .select(
                `
          id,
          full_name,
          email
        `
              )
              .in('id', customerIds);

            if (profileError) {
              console.error(
                'Profile Fetch Error:',
                profileError
              );
            }

            customerProfiles?.forEach(
              (profile) => {
                if (
                  groupedCustomers[profile.id]
                ) {
                  groupedCustomers[
                    profile.id
                  ].name =
                    profile.full_name ||
                    'Unknown User';

                  groupedCustomers[
                    profile.id
                  ].email =
                    profile.email || '';
                }
              }
            );
          }

          const finalGroups =
            Object.values(groupedCustomers);

          console.log(
            'Final Customer Groups:',
            finalGroups
          );

          setCustomerGroups(finalGroups);

          // =========================
          // APPOINTMENTS
          // =========================

          const {
            data: allAppointments,
            error: allAppointmentsError,
          } = await supabase
            .from('appointments')
            .select('*')
            .in('status', [
              'scheduled',
              'pending',
            ])
            .order('start_time', {
              ascending: true,
            })
            .limit(20);

          if (allAppointmentsError) {
            console.error(
              'Error fetching admin appointments:',
              allAppointmentsError.message
            );
          }

          setUpcomingAppointments(
            allAppointments || []
          );
        } else {
          // --- Regular user dashboard data ---
          const { data: userRequests, error: requestsError } = await supabase
            .from('service_requests')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (requestsError) console.error('Error fetching user requests:', requestsError.message);
          setActiveRequests(userRequests || []);

          const { data: userAppointments, error: appointmentsError } = await supabase
            .from('appointments')
            .select('*')
            .eq('user_id', user.id)
            .in('status', ['scheduled', 'pending'])
            .order('start_time', { ascending: true })
            .limit(20);




          if (appointmentsError) console.error('Error fetching user appointments:', appointmentsError.message);
          setUpcomingAppointments(userAppointments || []);

          try {
            const { data: dbInvoices, error: dbErr } = await supabase
              .from('invoices')
              .select('*')
              .order('created_at', { ascending: false });

            const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
            const allAvailable = (!dbErr && dbInvoices && dbInvoices.length > 0)
              ? Array.from(new Map([...dbInvoices, ...local].map(i => [i.id, i])).values())
              : local;

            const matched = allAvailable.filter(inv =>
              inv.user_id === user.id ||
              (inv.user_email && user.email && inv.user_email.toLowerCase() === user.email.toLowerCase())
            );

            setUserInvoices(matched.length > 0 ? matched : allAvailable);
          } catch (e) {
            const local = JSON.parse(localStorage.getItem('nlb_invoices') || '[]');
            const matched = local.filter(inv =>
              inv.user_id === user.id ||
              (inv.user_email && user.email && inv.user_email.toLowerCase() === user.email.toLowerCase())
            );
            setUserInvoices(matched.length > 0 ? matched : local);
          }
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, navigate]);


  if (!user) return null;

  const handleRequestClick = (request) => {
    navigate(`/request/${request.id}`);
  };

  const handleRescheduleAppointment = async () => {
    try {
      if (!selectedAppointment || !newStartTime) return;

      const { error } = await supabase
        .from('appointments')
        .update({
          start_time: newStartTime,
          status: 'pending'
        })
        .eq('id', selectedAppointment.id);

      if (error) throw error;
      await sendRescheduleEmail({
        appointmentId: selectedAppointment.id,
        newStartTime
      });

      setUpcomingAppointments(prev =>
        prev.map(app =>
          app.id === selectedAppointment.id
            ? {
              ...app,
              start_time: newStartTime,
              status: 'pending'
            }
            : app
        )
      );

      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setNewStartTime('');
    } catch (err) {
      alert(err.message);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);

      if (error) throw error;

      if (newStatus === 'completed') {
        setUpcomingAppointments(prev =>
          prev.filter(app => app.id !== appointmentId)
        );
      } else {
        setUpcomingAppointments(prev =>
          prev.map(app =>
            app.id === appointmentId
              ? { ...app, status: newStatus }
              : app
          )
        );
      }

      setActiveNotification(null);
    } catch (err) {
      alert('Failed to update appointment: ' + err.message);
    }
  };


  const handleCloseModal = () => {
    setShowRequestDetailModal(false);
    setSelectedRequest(null);
  };

  // âœ… UPDATED: Function now uses the external API module for better code organization
  const updateRequestStatus = async (requestId, newStatus) => {
    const originalRequests = [...activeRequests];

    // Optimistic UI update for immediate feedback
    setActiveRequests(prevRequests =>
      prevRequests.map(req =>
        req.id === requestId ? { ...req, status: newStatus } : req
      )
    );
    handleCloseModal();

    // Call the centralized API function
    const { data, error } = await serviceRequestsApi.updateStatus(requestId, newStatus);

    if (error) {
      console.error(`API Error on status update:`, error.message);
      alert(`Failed to update request: ${error.message}. Please try again.`);
      setActiveRequests(originalRequests);
    } else {
      const updatedId = data?.id || requestId;
      const updatedStatus = data?.status || newStatus;
      console.log(`âœ… Request ${updatedId} successfully updated to status: ${updatedStatus}`);
    }

  };


  const handleEnquiryStatusUpdate = async (userId, newStatus) => {
    try {
      const userToUpdate = enquiredUsers.find(u => u.id === userId);
      if (!userToUpdate) return alert('User not found.');

      // Step 1: Update Supabase
      const { error } = await supabase
        .from('request_user')
        .update({ status: newStatus })
        .eq('id', userId);

      if (error) {
        console.error('Error updating enquiry:', error.message);
        alert('Failed to update enquiry status: ' + error.message);
        return;
      }

      // Step 2: Update UI instantly
      setEnquiredUsers(prev =>
        prev.map(u =>
          u.id === userId ? { ...u, status: newStatus } : u
        )
      );
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('An unexpected error occurred.');
    }
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'scheduled': return 'text-yellow-600 bg-yellow-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved':
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  <AnimatePresence>
    {showRescheduleModal && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md"
        >
          <h3 className="text-xl font-bold mb-4">
            Reschedule Appointment
          </h3>

          <input
            type="datetime-local"
            value={newStartTime}
            min={new Date().toISOString().slice(0, 16)}
            onChange={(e) => setNewStartTime(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />

          <div className="flex gap-3">
            <button
              onClick={handleRescheduleAppointment}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg"
            >
              Save
            </button>

            <button
              onClick={() => setShowRescheduleModal(false)}
              className="flex-1 bg-gray-200 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>



  // --------------------------------------------------------------------
  // ADMIN DASHBOARD VIEW
  // --------------------------------------------------------------------
  if (role === 'admin') {
    return (

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence>
            {showRescheduleModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              >
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md"
                >
                  <h3 className="text-xl font-bold mb-4">
                    Reschedule Appointment
                  </h3>

                  <input
                    type="datetime-local"
                    value={newStartTime}
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={(e) => setNewStartTime(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={handleRescheduleAppointment}
                      className="flex-1 bg-indigo-600 text-white py-2 rounded-lg"
                    >
                      Save
                    </button>

                    <button
                      onClick={() => setShowRescheduleModal(false)}
                      className="flex-1 bg-gray-200 py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <SafeIcon icon={FiBarChart2} className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600 mt-1">Platform overview and system analytics</p>
              </div>
            </div>
          </motion.div>
          <AnimatePresence>
            {activeNotification && (
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed bottom-8 right-8 z-[100] bg-indigo-600 text-white p-6 rounded-2xl shadow-2xl max-w-sm border-2 border-indigo-400"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-ping" />
                    <h4 className="font-bold text-lg">Meeting Starting Now</h4>
                  </div>
                  <button onClick={() => setActiveNotification(null)} className="text-white/70 hover:text-white">
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
                <p className="text-indigo-100 text-sm mb-4">
                  Your appointment <strong>{activeNotification.title}</strong> is starting.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => updateAppointmentStatus(activeNotification.id, 'completed')}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 py-2 rounded-lg font-bold text-sm transition-colors"
                  >
                    Mark Completed
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Admin Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Total Users</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {adminStats.userCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiUsers} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-green-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Service Requests</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {adminStats.requestCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiTrendingUp} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Appointments</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {adminStats.appointmentCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiCalendar} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Admin Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Link to="/admin/users" className="block group">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all hover:scale-105 duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                        <SafeIcon icon={FiUsers} className="text-white text-xl" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">User Management</h3>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-blue-600 text-xl group-hover:translate-x-2 transition-transform" />
                  </div>
                  <p className="text-gray-600 text-sm">Manage all platform users and roles</p>
                </div>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Link to="/admin/analytics" className="block group">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all hover:scale-105 duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                        <SafeIcon icon={FiBarChart2} className="text-white text-xl" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Analytics</h3>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-green-600 text-xl group-hover:translate-x-2 transition-transform" />
                  </div>
                  <p className="text-gray-600 text-sm">View service usage and system metrics</p>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* 💳 Admin Customer Invoices & Payment Settlement Ledger */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-indigo-100 p-8 mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Financial Overview</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Customer Invoices & Payment Ledger</h2>
                <p className="text-xs text-gray-500">Track all customer billing settlements, paid receipts, and pending invoices in real-time</p>
              </div>

              <div className="flex gap-3 flex-wrap">
                <div className="bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-xl text-right">
                  <span className="text-[10px] font-bold text-emerald-800 uppercase block">Collected Revenue</span>
                  <span className="text-lg font-black text-emerald-700">
                    ${allInvoicesAdmin.filter(i => i.status === 'paid').reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl text-right">
                  <span className="text-[10px] font-bold text-amber-800 uppercase block">Pending Settlements</span>
                  <span className="text-lg font-black text-amber-700">
                    {allInvoicesAdmin.filter(i => i.status !== 'paid').length} Invoices
                  </span>
                </div>
              </div>
            </div>

            {allInvoicesAdmin.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-y border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Invoice #</th>
                      <th className="py-3 px-4">Customer Details</th>
                      <th className="py-3 px-4">Category & Title</th>
                      <th className="py-3 px-4">Amount Due</th>
                      <th className="py-3 px-4">Payment Status</th>
                      <th className="py-3 px-4">Gateway / Date</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {allInvoicesAdmin.map((inv) => {
                      const matchedCust = allCustomers.find(c => c.id === inv.user_id || (c.email && inv.user_email && c.email.toLowerCase() === inv.user_email.toLowerCase()));
                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4 font-bold text-indigo-600">
                            {inv.invoice_number}
                          </td>
                          <td className="py-3.5 px-4">
                            <p className="font-semibold text-gray-900">{matchedCust?.full_name || inv.customer_name || 'Customer'}</p>
                            <p className="text-xs text-gray-500">{inv.user_email || matchedCust?.email || inv.user_id}</p>
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="inline-block text-[10px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-100 mb-1">
                              {inv.category || 'Billing'}
                            </span>
                            <p className="font-medium text-gray-900 text-xs">{inv.title}</p>
                          </td>
                          <td className="py-3.5 px-4 font-black text-gray-900">
                            {inv.currency === 'INR' ? '₹' : '$'}{inv.amount}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-amber-100 text-amber-800 border border-amber-200'
                              }`}>
                              {inv.status === 'paid' ? '✔ PAID' : '⏳ UNPAID'}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-xs text-gray-600">
                            <p className="font-medium">{inv.payment_method || (inv.status === 'paid' ? 'Stripe Pay' : 'Pending Gateway')}</p>
                            <p className="text-[11px] text-gray-400">{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : new Date(inv.created_at).toLocaleDateString()}</p>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            {inv.user_id && (
                              <button
                                onClick={() => navigate(`/admin/customer/${inv.user_id}`)}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
                              >
                                Manage Customer →
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-gray-200">
                <p className="text-sm font-semibold text-gray-600">No raised customer invoices found yet</p>
                <p className="text-xs text-gray-400 mt-1">Invoices raised in Customer Details will appear in this ledger in real-time.</p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Customer Request Groups
              </h2>

              <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold">
                {customerGroups.length} Customers
              </span>
            </div>

            <div className="space-y-4">
              {customerGroups.map((customer) => (
                <div
                  key={customer.user_id}
                  className="border rounded-xl p-5 hover:shadow-md transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {customer.name || "Unknown User"}
                      </h3>

                      <p className="text-sm text-gray-500">
                        {customer.email}
                      </p>

                      <div className="flex gap-3 mt-3 flex-wrap">
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">
                          Total: {customer.total_requests}
                        </span>

                        <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs">
                          Pending: {customer.pending}
                        </span>

                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs">
                          Completed: {customer.completed}
                        </span>

                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs">
                          In Progress: {customer.in_progress}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        navigate(
                          `/admin/customer/${customer.user_id}`
                        )
                      }
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg"
                    >
                      View Customer
                    </button>
                  </div>

                  <div className="mt-4 border-t pt-4">
                    {customer.requests.map((req) => (
                      <div
                        key={req.id}
                        className="flex justify-between py-2"
                      >
                        <span>
                          {req.service_type
                            ?.replace(/-/g, " ")
                            .replace(/\b\w/g, (c) =>
                              c.toUpperCase()
                            )}
                        </span>

                        <span
                          className={`px-2 py-1 rounded text-xs ${getStatusColor(
                            req.status
                          )}`}
                        >
                          {req.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Active Requests (Admin View - all requests) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8"
          >

            <div className="flex items-center justify-between mb-6">

              <div className="flex items-center">

                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiHome} className="text-white text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Active Service Requests</h2>
              </div>
              <div className="flex items-center space-x-3">
                {role === 'admin' && (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowAddRequestModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all flex items-center gap-1.5"
                  >
                    <SafeIcon icon={FiPlus} className="text-base" />
                    Add Request
                  </motion.button>
                )}
                <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
                  {activeRequests.length} Total
                </span>
              </div>
            </div>
            {activeRequests.length > 0 ? (
              <div className="space-y-4">
                {activeRequests.map((request, index) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-indigo-200 transition-all bg-gradient-to-r from-white to-gray-50 cursor-pointer"
                    onClick={() => handleRequestClick(request)} // Open modal on click
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiHome} className="text-xl text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-semibold text-gray-800 tracking-wide capitalize">{request.service_type
                            ?.replace(/-/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase())}</h3>
                          <span className={`px-4 py-1 rounded-full text-xs font-semibold ${getStatusColor(request.status)}`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 flex items-center">
                          <SafeIcon icon={FiCalendar} className="mr-2" />
                          Due: {new Date(request.end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <SafeIcon icon={FiCheckCircle} className="mx-auto text-5xl text-green-400 mb-4" />
                <h3 className="text-xl font-bold text-gray-800">All Clear!</h3>
                <p className="text-gray-500 mt-2">There are no service requests to review at the moment.</p>
              </div>
            )}
          </motion.div>

          {/* âœ… Upcoming Appointments (Admin View - all appointments) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiCalendar} className="text-white text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Upcoming Appointments</h2>
              </div>
              <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
                {upcomingAppointments.length} Scheduled
              </span>
            </div>

            {upcomingAppointments.length > 0 ? (
              <div className="space-y-4">
                {upcomingAppointments.map((app, index) => {
                  // Calculate if the appointment time has passed
                  const appDate = new Date(app.start_time);
                  const isPast = Date.now() >= appDate.getTime();
                  const isCompleted = app.status?.toLowerCase() === 'completed';


                  return (
                    <motion.div
                      key={app.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`border rounded-xl p-5 hover:shadow-lg transition-all bg-gradient-to-r from-white to-gray-50 ${isPast && !isCompleted ? 'border-orange-300 ring-1 ring-orange-100' : 'border-gray-200'
                        }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${isCompleted ? 'from-green-100 to-emerald-100' : 'from-yellow-100 to-orange-100'
                            }`}>
                            <SafeIcon
                              icon={isCompleted ? FiCheckCircle : FiClock}
                              className={`text-xl ${isCompleted ? 'text-emerald-600' : 'text-orange-600'}`}
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-gray-900">{app.title || 'Appointment'}</h3>
                              <span className={`px-3 py-0.5 rounded-full text-xs font-bold ${getStatusColor(app.status)}`}>
                                {app.status}
                              </span>
                            </div>

                            {/* Display User Details (Joined from profiles table) */}
                            <div className="mt-1 flex flex-col gap-1">
                              <p className="text-sm text-gray-600 flex items-center">
                                <SafeIcon icon={FiCalendar} className="mr-2 text-orange-500" />
                                {appDate.toLocaleString()}
                              </p>
                              {app.user && (
                                <p className="text-sm text-indigo-600 font-medium flex items-center">
                                  <SafeIcon icon={FiUsers} className="mr-2" />
                                  {app.user.full_name || app.user.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ACTION BUTTONS: Show only if time has passed and not yet completed */}
                        {isPast && !isCompleted && (
                          <div className="flex items-center gap-2 self-end md:self-center">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => updateAppointmentStatus(app.id, 'completed')}
                              className="flex items-center bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md transition-all"
                            >
                              <SafeIcon icon={FiCheckCircle} className="mr-2" /> Complete
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedAppointment(app);
                                setShowRescheduleModal(true);
                              }}
                              className="flex items-center bg-white border border-orange-400 text-orange-600 hover:bg-orange-50 px-4 py-2 rounded-lg text-sm font-bold transition-all"
                            >
                              Reschedule
                            </motion.button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10">
                <SafeIcon icon={FiCalendar} className="mx-auto text-5xl text-gray-400 mb-4" />
                <h3 className="text-xl font-bold text-gray-800">No Upcoming Appointments</h3>
                <p className="text-gray-500 mt-2">The schedule is clear for now.</p>
              </div>
            )}
          </motion.div>

          {/* ✅ Enquired Users Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mt-8"

          >

            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiUsers} className="text-white text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Enquired Users
                </h2>
              </div>


              <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
                {enquiredUsers.length} Total
              </span>


            </div>

            {enquiredUsers.length > 0 ? (<div className="space-y-4">
              {enquiredUsers.map((user, index) => {
                const isPending = user.status === 'Pending';
                const isContacted = user.status === 'Contacted';


                return (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className={`border rounded-xl p-5 hover:shadow-lg transition-all ${isPending
                      ? 'border-amber-400 bg-gradient-to-r from-amber-50/50 via-orange-50/10 to-white ring-2 ring-amber-400/20'
                      : 'border-gray-200 bg-gradient-to-r from-white to-gray-50/50 hover:border-indigo-200'
                      }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <h3 className="text-lg font-bold text-gray-900">
                            {user.name || 'Unnamed User'}
                          </h3>

                          {isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-300">
                              Pending
                            </span>
                          ) : isContacted ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300">
                              Contacted
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 ring-1 ring-gray-300">
                              {user.status}
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 font-medium">
                          {user.email || 'No email provided'}
                        </p>

                        <p className="text-sm text-gray-700 mt-1">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-2">
                            Phone:
                          </span>
                          <span className="font-semibold text-gray-900">
                            {user.phone}
                          </span>
                        </p>

                        <p className="text-xs text-gray-400 mt-2 flex items-center">
                          <SafeIcon
                            icon={FiClock}
                            className="mr-1 text-gray-400"
                          />
                          Enquired:{' '}
                          {new Date(user.created_at).toLocaleString()}
                        </p>

                        {user.description && (
                          <div className="mt-3 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100 text-sm">
                            <span className="text-xs font-bold text-indigo-800 uppercase tracking-wider block mb-1">
                              Enquiry Requirements:
                            </span>
                            <p className="text-gray-700 italic">
                              "{user.description}"
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Status Dropdown */}
                      <div className="flex items-center gap-2 self-end sm:self-start bg-white/80 backdrop-blur-sm p-1.5 rounded-xl border border-gray-100 shadow-sm">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
                          Status:
                        </label>

                        <select
                          value={user.status || 'Pending'}
                          onChange={(e) =>
                            handleEnquiryStatusUpdate(
                              user.id,
                              e.target.value
                            )
                          }
                          className={`text-sm font-bold rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all cursor-pointer ${isPending
                            ? 'bg-amber-500 border-amber-600 text-white hover:bg-amber-600'
                            : 'bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700'
                            }`}
                        >
                          <option value="Pending">
                            Pending
                          </option>
                          <option value="Contacted">
                            Contacted
                          </option>
                        </select>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>


            ) : (<div className="text-center py-10"> <SafeIcon
              icon={FiUsers}
              className="mx-auto text-5xl text-gray-400 mb-4"
            /> <h3 className="text-xl font-bold text-gray-800">
                No Enquiries Found </h3> <p className="text-gray-500 mt-2">
                No new user enquiries to review. </p> </div>
            )}
          </motion.div>







        </div>

        {showAddRequestModal && (
          <AdminAddRequestModal
            isOpen={showAddRequestModal}
            onClose={() => setShowAddRequestModal(false)}
            customers={allCustomers}
            onSuccess={(newReq) => {
              setActiveRequests((prev) => [newReq, ...prev]);
            }}
          />
        )}
      </div>
    );
  }

  // --------------------------------------------------------------------
  // PROVIDER DASHBOARD VIEW
  // --------------------------------------------------------------------
  if (role === 'provider') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <SafeIcon icon={FiBarChart2} className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Provider Dashboard</h1>
                <p className="text-gray-600 mt-1">Monitor and manage service activity</p>
              </div>
            </div>
          </motion.div>

          {/* Provider Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* Total Users */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Total Users</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {adminStats.requestCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiUsers} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>

            {/* Total Requests */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-green-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Service Requests</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {adminStats.requestCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiTrendingUp} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>

            {/* Appointments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Appointments</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {adminStats.appointmentCount}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiCalendar} className="text-3xl text-white" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Active Service Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiHome} className="text-white text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Active Service Requests</h2>
              </div>
              <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
                {activeRequests.length} Total
              </span>
            </div>

            {activeRequests.length > 0 ? (
              <div className="space-y-4">
                {activeRequests.map((request, index) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-indigo-200 transition-all bg-gradient-to-r from-white to-gray-50 cursor-pointer"
                    onClick={() => navigate(`/request/${request.id}`)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiHome} className="text-xl text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{request.service_type
                            ?.replace(/-/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase())}</h3>
                          <span
                            className={`px-4 py-1 rounded-full text-xs font-semibold ${getStatusColor(request.status)}`}
                          >
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Created: {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <SafeIcon icon={FiCheckCircle} className="mx-auto text-5xl text-green-400 mb-4" />
                <h3 className="text-xl font-bold text-gray-800">No Active Requests</h3>
                <p className="text-gray-500 mt-2">All services are up to date.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }


  // --------------------------------------------------------------------
  // USER DASHBOARD VIEW
  // --------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-indigo-100">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <SafeIcon icon={FiUsers} className="text-3xl text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Welcome back, {user.full_name || 'User'}</h1>
                <p className="text-gray-600 mt-1 text-lg">Here's what's happening with your services</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg border border-green-100 p-8 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Completed Requests</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {activeRequests.filter(r => r.status?.toLowerCase() === 'completed' || r.status?.toLowerCase() === 'accepted').length}
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiCheckCircle} className="text-3xl text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-yellow-100 p-8 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Appointments</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  {upcomingAppointments.length}
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiCalendar} className="text-3xl text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 hover:shadow-xl transition-all"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending Requests</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {activeRequests.filter(a => a.status?.toLowerCase() === 'pending').length}
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiAlertCircle} className="text-3xl text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* âœ… User Active Requests (User Only) */}
        {activeRequests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiHome} className="text-white text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Your Service Requests</h2>
              </div>
              <Link to="/request-service" className="bg-indigo-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center">
                <SafeIcon icon={FiPlus} className="mr-2" /> New Request
              </Link>
            </div>
            <div className="space-y-4">
              {activeRequests.map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-indigo-200 transition-all bg-gradient-to-r from-white to-gray-50 cursor-pointer"
                  onClick={() => handleRequestClick(request)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiHome} className="text-xl text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{request.service_type
                          ?.replace(/-/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())}</h3>
                        <span className={`px-4 py-1 rounded-full text-xs font-semibold ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{request.description || 'Click to see details'}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ✅ User Raised Invoices (Customer Only - Strictly Private) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="bg-white rounded-2xl shadow-xl border border-indigo-100 p-8 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
                <SafeIcon icon={FiTrendingUp} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Your Invoices & Billing</h2>
                <p className="text-xs text-gray-500">Official billing raised by platform administration for your account</p>
              </div>
            </div>
            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
              {userInvoices.filter(i => i.status === 'unpaid').length} Pending Payment
            </span>
          </div>

          {userInvoices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userInvoices.map((inv) => (
                <div key={inv.id} onClick={() => navigate(inv.status === "paid" ? `/order-confirmation?invoice=${inv.id}` : `/checkout?invoice=${inv.id}`)} className="border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-indigo-300 transition-all bg-gradient-to-r from-white to-slate-50/50 flex flex-col justify-between cursor-pointer">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                            {inv.invoice_number}
                          </span>
                          {inv.category && (
                            <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100">
                              {inv.category}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mt-1">{inv.title}</h3>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                        {inv.status}
                      </span>
                    </div>

                    {(inv.total_fee || inv.advance_paid) && (
                      <div className="flex gap-4 p-2 bg-slate-100/60 rounded-lg text-[11px] font-semibold text-gray-600 mb-3">
                        {inv.total_fee && <span>Total: {inv.currency === 'INR' ? '₹' : '$'}{inv.total_fee}</span>}
                        {inv.advance_paid && <span className="text-emerald-700">Advance Paid: {inv.currency === 'INR' ? '₹' : '$'}{inv.advance_paid}</span>}
                      </div>
                    )}

                    {inv.description && (
                      <p className="text-xs text-gray-600 mb-4">{inv.description}</p>
                    )}
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between mt-2">
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Payable Amount Due</p>
                      <p className="text-xl font-black text-gray-900">
                        {inv.currency === 'INR' ? '₹' : '$'}{inv.amount}
                      </p>
                    </div>
                    {inv.status === 'unpaid' ? (
                      <button
                        onClick={() => navigate(`/checkout?invoice=${encodeURIComponent(inv.id)}`, { state: { invoice: inv } })}
                        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5"
                      >
                        <span>Pay Now</span>
                        <SafeIcon icon={FiArrowRight} />
                      </button>
                    ) : (
                      <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                        <SafeIcon icon={FiCheckCircle} /> Paid
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed border-gray-200 rounded-xl bg-slate-50/50">
              <SafeIcon icon={FiTrendingUp} className="mx-auto text-4xl text-gray-300 mb-2" />
              <h3 className="text-base font-bold text-gray-700">No Invoices Yet</h3>
              <p className="text-xs text-gray-400 mt-1">There are currently no billing statements or raised invoices for your account.</p>
            </div>
          )}
        </motion.div>

        {/* User Upcoming Appointments */}
        {upcomingAppointments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                <SafeIcon icon={FiCalendar} className="text-white text-lg" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Your Upcoming Appointments</h2>
            </div>
            <div className="space-y-4">
              {upcomingAppointments.map((app, index) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-yellow-200 transition-all bg-gradient-to-r from-white to-yellow-50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiClock} className="text-xl text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{app.title || 'Scheduled Appointment'}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(app.start_time).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* No Data State for User */}
        {activeRequests.length === 0 && upcomingAppointments.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
          >
            <SafeIcon icon={FiHome} className="mx-auto text-5xl text-indigo-400 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800">Your dashboard is looking fresh!</h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">You don't have any active requests or upcoming appointments. Ready to get started?</p>
            <Link to="/request-service" className="mt-6 inline-flex items-center bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors shadow-lg">
              <SafeIcon icon={FiPlus} className="mr-2" /> Request a New Service
            </Link>
          </motion.div>
        )}
        {/* ... Other user sections ... */}
      </div>


      {showAddRequestModal && (
        <AdminAddRequestModal
          isOpen={showAddRequestModal}
          onClose={() => setShowAddRequestModal(false)}
          customers={allCustomers}
          onSuccess={(newReq) => {
            setActiveRequests((prev) => [newReq, ...prev]);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;